import { forEach, find, findLast, filter, includes,
         head, last, indexOf, compact } from 'lodash';

import { OwnedObject, flatten, flatMap, invokeMap$ } from 'gear';

import { Xml, Metadata } from '../util';

import { Figure, updateMethods, updateArray$ } from '../figure';

import { Layer, Layers, topTargetLayer } from './layer';
import { ContentLayer } from './content-layer';

export const Page = OwnedObject.extend( updateMethods(), { typeName: 'Page', __debugProps: true,

  Properties: {
    id      : { type:'string' , def: null, dropDef: true },
    name    : { type:'string' , def: null, dropDef: true },
    locked  : { type:'boolean', def:false, dropDef: true },
    hidden  : { type:'boolean', def:false, dropDef: true },
    xType   : { type: 'string', def: null, dropDef: true },
    activeLayer : { type: 'Layer', def: null, owned: false,
                    get() {
                      if( !this._activeLayer ) {
                        this._activeLayer = this.initialActiveLayer();
                      }
                      return this._activeLayer;
                    },
                    manualParsing: true,
                    manualCloning: true,
                    onChange() {
                      const o = this._owner;
                      if( o && o._activePage === this ) { // Forward if active page
                        o._activeLayerChanged = true;
                        o.fireEvent('activelayerchanged',o,this.activeLayer);
                      }
                    } },
    layers : { type: 'Layer[]', def: [],
      onChange( added,removed) {
        this.lock();

        const doc = this.document;
        if( doc ) {
          doc.fireEvent('layerschanged',doc,added,removed);
        }

        if( ! includes(this._layers,this._activeLayer) ) {
          this.activeLayer = last(this._layers);
        }
        const o = this._owner;
        if( o && o._activePage === this ) { // Forward if active page
          o.fireEvent('activelayerchanged',o,this.activeLayer);
        }
      },
      member: '_layers',
      manualParsing: true
    },
    metadata: { type: 'Metadata', def: new Metadata({}), manualParsing: true }
  },

  get isPage() { return true; },

  get activeLayerIndex() {
    return indexOf(this._layers,this.activeLayer);
  },
  set activeLayerIndex( i ) {
    this.activeLayer = this._layers[i];
  },

  initialActiveLayer() {
    const layers = this._layers;
    return findLast( layers, layer => {
      return layer.visible && layer instanceof ContentLayer.Figure;
    }) || topTargetLayer( layers ) || last(layers);
  },

  get visibleFigures() {
    return filter( Layers.flatten( filter(this.layers,'visible') ), 'visible' );
  },

  get figures() {
    return Layers.flatten(this.layers);
  },

  forEachFigure( func ) {
    const { layers } = this;
    for( let k = 0, kEnd = layers.length; k < kEnd; ++k ) {
      const stop = layers[k].forEachFigure(func);
      if( stop ) {
        return false;
      }
    }
  },

  // [TODO] This decides if the page is encoded in the CDL, it is really weak
  isDefault() {
    return !this._id && !this._name && !this._xType && !this._locked && !this._hidden &&
           this._metadata.isEmpty;
  },

  get document() {
    return this._owner;
  },

  get visible() {}, // [TODO] ?
  get active() {},

  clone() {
    const ali = this.activeLayerIndex;
    const c = new this.constructor(this);
    c._activeLayer = c._layers[ali];
    return c;
  },

  buildXml( node ) {
    return Xml.addObject(node,this,'Page');
  },

  setupXml( node ) {
    Xml.setupProperties(node,this);
    this._metadata.setupXml(node);
    const layersNode = Xml.appendElement(node,'Layers') ;
    Xml.addObjectArray(layersNode,this._layers);
    return node;
  },

  init( config ) {
    this.callParent(config);
    const layers = this._layers;
    if( layers.length === 0 ) {
      layers.push( new Layer() );
    }
  },
  release( ls ) {
    return ls===undefined ? this._layers.clear() : this._layers.remove(ls);
  },
  lock( originator ) {
    if( this._owner ) {
      this._owner.lock(originator||this);
    }
  },

  update$( type ) {
    return updateArray$(this._layers,type);
  },

  bounds$() {
    return Layers.bounds$( filter( this.layers, layer => layer.figures.length > 0 ) );
  },

  layerBy( selector) {
    return find(this._layers,selector );
  },
  prepareToDraw$( zoom ) {
    return invokeMap$(this._layers,'prepareToDraw$',zoom);
  },

  spawnContentLayersFor( figure, point ) {
    const { layers } = this;
    const { layer } = figure;

    // The layer will be empty after this point, to avoid clonning
    const content = layer.spawnContentLayersFor(figure,point);

    const content_layers = content.layers,
          content_active = content.active;

    const prev = content_layers.indexOf(content_active);

    this.contentInfo(content_active,{
      prev: prev,
      next: content_layers.length - prev - 1
    });

    // Insert content layers after the edited layer and make it invisible
    layers.insert(layers.indexOf(layer)+1,content_layers);
    this.activeLayer = content_active;

    // We only leave the original figure, that will be used as reference
    // when assembling back the content layers
    layer.hidden = true;

    return this;
  },
  editingContent() {
    return this.activeLayer instanceof ContentLayer;
  },
  contentInfo( layer, info ) {
    // We resort to metadata, so this is saved directly as part of CDL
    // This allows for seamesly history and can documents can even be saved
    // in a content edition state if required, for example to serialize
    // current state

    const metadata = layer.metadata;

    if( info === undefined ) {
      return {
        prev:   metadata.get('contentPrev'),
        next:   metadata.get('contentNext')
      };
    }
    else {
      metadata.set('contentPrev',info.prev);
      metadata.set('contentNext',info.next);
      return this;
    }
  },
  commitContent$() {
    const _this = this,
        contentLayer = _this.activeLayer,
        layers = _this._layers,
        info = _this.contentInfo(contentLayer),
        contentIdx = layers.indexOf(contentLayer),
        originalLayer = layers[ contentIdx - info.prev - 1 ],
        originalFigure = originalLayer.release()[0];

    // Replace the old figure with the new assembled one
    return contentLayer.assemble$(originalFigure) .then( figure => {

    const content_layers = layers.slice( contentIdx-info.prev, contentIdx+info.next+1 );

    // Remove content layers and make the layer visible again
    layers.remove( content_layers );

    originalLayer.hidden = false;

    originalLayer.figures = compact( flatten([
      head(content_layers).release(), figure, last(content_layers).release()
    ]) );

    _this.activeLayer = originalLayer;

    return figure;
    });
  },
  revertContentLayer( layer ) {
    this._layers.replace(layer._contentLayers,layer._original);
    return this;
  },

});


Page.parseXml = function( node, bcFixes, PageType = Page ) {
  const p = Xml.parseProperties(node,PageType);
  const layersNode = Xml.child(node,'Layers');
  p.layers = layersNode ? Layer.arrayFromXml(layersNode,bcFixes) : [ new Layer() ] ;
  const page = new PageType(p);
  page._metadata = Metadata.parseXml(node,page);
  return page;
};


Page.arrayFromXml = function( node, bcFixes, PageType = Page ) {
  const pages = [];

  let notPagedLayers = [],
      notLayeredFigures = [];

  var clearNotLayeredFigures = function() {
    if( notLayeredFigures.length > 0) {
      // Create a new unnamed layer
      notPagedLayers.push( new Layer({ figures: notLayeredFigures }) );
      notLayeredFigures = [];
    }
  };

  var clearNotPagedLayers = function() {
    clearNotLayeredFigures();
    if( notPagedLayers.length > 0 ) {
      // Create a new unnamed page
      pages.push( new PageType({ layers: notPagedLayers }) );
      notPagedLayers = [];
    }
  };

  forEach( Xml.children_(node), child => {

    const name = child.nodeName;

    if( name === 'Page' ) {
      clearNotPagedLayers();

      pages.push( PageType.parseXml(child,bcFixes) );
    }
    if( name === 'Layer' ) {
      clearNotLayeredFigures();

      const layer = Layer.parseXml(child,bcFixes);
      if( layer ) {
        notPagedLayers.push(layer);
      }
    }
    else {
      const figure = Figure.parseXml(child,bcFixes);
      if( figure ) {
        notLayeredFigures.push( figure );
      }
    }
  });

  clearNotPagedLayers();

  if( pages.length === 0 ) {
    // There is always an implicit page, even if there are no layers on it
    pages.push( new PageType() );
  }
  return pages;
};

export const Pages = {};

Pages.flatten = function(pages) {
  return flatMap( pages, page => Layers.flatten(page.layers) );
};



/** WEBPACK FOOTER **
 ** ./src/cdl/document/page.js
 **/